import { use, useMemo, useState, useEffect, useRef, type MutableRefObject } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import { AncientRoadsLayer } from './passage/map/AncientRoadsLayer';
import { LatLngBounds, type LatLngExpression, type Map as LeafletMap, type Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AppDataContext } from '@/lib/app-data-hydration';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { MapPin, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { MapTimelineControl } from './passage/map/MapTimelineControl';
import { MobileEntityModal } from './passage/map/MobileEntityModal';
import { MapFilters } from './passage/map/MapFilters';
import { MapLegend } from './passage/map/MapLegend';
import { MapStyleControl, MAP_STYLES } from './passage/map/MapStyleControl';
import { createSimplePlaceIcon, createEventIcon } from './passage/map/map-icon-utils';
import { MapColorFilter, MapClickHandler, MapAnimationStopper } from './passage/map/MapControls';
import { MARKER_POPUP_ANIMATION_DELAY } from './passage/map/map-constants';
import { initializeAncientWorldMapTimeline, filterAncientWorldMapPlaces } from './passage/map/ancient-world-map-utils';
import { PlacePopupContent } from './passage/map/PlacePopupContent';
import { Analytics } from '@/lib/analytics';
import { EventPopupContent } from './passage/map/EventPopupContent';
import { useUserSettingsContext } from '@/lib/context/user-settings-context';


export default function AncientWorldMap() {
    const appDataContext = use(AppDataContext);

    // Check if data is loaded before rendering
    if (!appDataContext.data) {
        return <LoadingSkeleton />;
    }

    const allPlaces = appDataContext.data.places || [];
    const allEvents = appDataContext.data.historicalEvents || [];

    return <AncientWorldMapInner allPlaces={allPlaces} allEvents={allEvents} />;
}

function AncientWorldMapInner({ allPlaces, allEvents }: { allPlaces: Place[]; allEvents: HistoricalEvent[] }) {

    // UI State
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // User Settings
    const { settings } = useUserSettingsContext();
    const preferredChronology = settings?.preferredChronology;


    // Map Style State
    const [selectedStyleId, setSelectedStyleId] = useState('watercolor');
    const [showLabels, setShowLabels] = useState(true);
    const [showAncientRoads, setShowAncientRoads] = useState(false);
    const [showSeaLanes, setShowSeaLanes] = useState(false);

    // Filter State
    const [selectedPlaceTypes, setSelectedPlaceTypes] = useState<Set<string>>(new Set());
    const [selectedEventCategories, setSelectedEventCategories] = useState<Set<string>>(new Set());

    // Timeline State
    const [selectedYear, setSelectedYear] = useState<number>(-1000);
    const [timelineData, setTimelineData] = useState<{
        minYear: number;
        maxYear: number;
        eras: TimelineEra[];
    } | null>(null);

    // Map and marker refs for programmatic control
    const mapRef = useRef<LeafletMap | null>(null);
    const markerRefs = useRef<Map<string, LeafletMarker>>(new Map());
    const eventMarkerRefs = useRef<Map<string, LeafletMarker>>(new Map());

    const [selectedEntityForModal, setSelectedEntityForModal] = useState<Place | null>(null);
    const [selectedEventForModal, setSelectedEventForModal] = useState<any | null>(null);

    // Generic function to center map on a marker and open its popup
    const handleMarkerClick = (
        markerKey: string,
        coordinates: { latitude: number; longitude: number },
        markerRefsMap: MutableRefObject<Map<string, LeafletMarker>>,
        markerData?: any
    ) => {
        if (mapRef.current) {
            // Center the map on the marker (without changing zoom)
            mapRef.current.panTo([coordinates.latitude, coordinates.longitude], {
                animate: true,
                duration: 0.5
            });

            // On mobile, show modal instead of popup
            if (typeof window !== 'undefined' && window.innerWidth < 768 && markerData) {
                if ('placeName' in markerData) {
                    setSelectedEntityForModal(markerData);
                } else if ('title' in (markerData.event || {})) {
                    setSelectedEventForModal(markerData);
                }
            } else {
                // Open the marker's popup if it exists
                const marker = markerRefsMap.current.get(markerKey);
                if (marker) {
                    setTimeout(() => {
                        marker.openPopup();
                    }, MARKER_POPUP_ANIMATION_DELAY);
                }
            }

            // Close sidebar on mobile
            if (typeof window !== 'undefined' && window.innerWidth < 768) {
                setSidebarOpen(false);
            }
        }
    };

    // Wrapper functions for specific marker types
    const handlePlaceClick = (placeName: string, coordinates: { latitude: number; longitude: number }) => {
        Analytics.mapInteraction('marker_click', { marker_type: 'place', place_name: placeName });
        const place = allPlaces.find(p => p.placeName === placeName);
        handleMarkerClick(placeName, coordinates, markerRefs, place);
    };

    const handleEventClick = (eventTitle: string, coordinates: { latitude: number; longitude: number }) => {
        Analytics.mapInteraction('marker_click', { marker_type: 'event', event_title: eventTitle });
        const markerData = eventMarkers.find(e => e.event.title === eventTitle);
        handleMarkerClick(eventTitle, coordinates, eventMarkerRefs, markerData);
    };

    // Function to handle map clicks (close sidebar on mobile)
    const handleMapClick = () => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    // Initialize timeline and filters
    useEffect(() => {
        if (allPlaces.length === 0) {
            // Set default timeline data when no places are available
            setTimelineData({
                minYear: -2000,
                maxYear: 100,
                eras: []
            });
            return;
        }

        const { minYear, maxYear, eras, initialYear, placeTypes } = initializeAncientWorldMapTimeline(allPlaces);
        setTimelineData({ minYear, maxYear, eras });
        setSelectedYear(initialYear);
        setSelectedPlaceTypes(placeTypes);
    }, [allPlaces]);

    // Filter places and events by timeline and type
    const { visiblePlaces, placeTypeCounts, visibleEvents, eventCategoryCounts } = useMemo(() => {
        return filterAncientWorldMapPlaces(allPlaces, allEvents, selectedYear, selectedPlaceTypes, selectedEventCategories, preferredChronology);
    }, [allPlaces, allEvents, selectedYear, selectedPlaceTypes, selectedEventCategories, preferredChronology]);


    // Calculate map bounds
    const { initialCenter, initialBounds } = useMemo(() => {
        const DEFAULT_CENTER: LatLngExpression = [31.7683, 35.2137]; // Jerusalem

        if (visiblePlaces.length === 0) {
            return { initialCenter: DEFAULT_CENTER, initialBounds: null };
        }

        const bounds = new LatLngBounds(
            visiblePlaces.map(p => [p.coordinates.latitude, p.coordinates.longitude] as LatLngExpression)
        );

        return { initialCenter: bounds.getCenter(), initialBounds: bounds };
    }, [visiblePlaces]);

    // Create place markers
    const placeMarkers = useMemo(() => {
        return visiblePlaces.map(place => ({
            place,
            position: [place.coordinates.latitude, place.coordinates.longitude] as LatLngExpression
        }));
    }, [visiblePlaces]);

    // Create place name lookup for efficient event-to-place matching
    const placeNameLookup = useMemo(() => {
        const lookup = new Map<string, Place>();
        visiblePlaces.forEach(place => {
            lookup.set(place.placeName.toLowerCase(), place);
        });
        return lookup;
    }, [visiblePlaces]);

    // Create event markers - match events to places for positioning
    const eventMarkers = useMemo(() => {
        return visibleEvents
            .map(event => {
                // Try to find a matching place for this event
                let matchingPlace: Place | undefined;

                // First try: exact match with relatedPlaces
                if (event.relatedPlaces && event.relatedPlaces.length > 0) {
                    for (const placeName of event.relatedPlaces) {
                        matchingPlace = placeNameLookup.get(placeName.toLowerCase());
                        if (matchingPlace) break;
                    }
                }

                // Second try: check if any place name is in the event title
                if (!matchingPlace) {
                    const eventTitleLower = event.title.toLowerCase();
                    for (const [placeName, place] of placeNameLookup) {
                        if (eventTitleLower.includes(placeName)) {
                            matchingPlace = place;
                            break;
                        }
                    }
                }

                if (!matchingPlace) return null;

                return {
                    event,
                    position: [matchingPlace.coordinates.latitude, matchingPlace.coordinates.longitude] as LatLngExpression,
                    matchingPlace
                };
            })
            .filter((marker): marker is NonNullable<typeof marker> => marker !== null);
    }, [visibleEvents, placeNameLookup]);

    // Get available types and categories
    const availablePlaceTypes = useMemo(() => {
        return Array.from(new Set(allPlaces.map(p => p.type))) as string[];
    }, [allPlaces]);

    const availableEventCategories = useMemo(() => {
        const categories = new Set<string>();
        allEvents.forEach(event => {
            event.categories.forEach(cat => categories.add(cat.category));
        });
        return Array.from(categories);
    }, [allEvents]);

    const currentStyle = MAP_STYLES.find(s => s.id === selectedStyleId) || MAP_STYLES[1];

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Wait for timeline to initialize
    if (!timelineData) {
        return <LoadingSkeleton />;
    }

    return (
        <div className="flex flex-col h-[calc(100vh-48px)]">
            {/* Header */}
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h1 className="text-xl font-bold">Ancient World Map</h1>
                </div>

                <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                        {visiblePlaces.length} places
                    </Badge>
                    <Badge variant="secondary">
                        {visibleEvents.length} events
                    </Badge>

                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                        {sidebarOpen ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
                    </Button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Map */}
                <div className="flex-1 relative">
                    <MapContainer
                        center={initialCenter}
                        zoom={6}
                        style={{ height: '100%', width: '100%', zIndex: 0 }}
                        zoomControl={false}
                        ref={mapRef}
                    >
                        <TileLayer
                            url={currentStyle.url}
                            attribution={currentStyle.attribution}
                        />

                        <MapColorFilter colorScheme="ancient" />
                        <MapClickHandler onMapClick={handleMapClick} />
                        <MapAnimationStopper />
                        <AncientRoadsLayer show={showAncientRoads} showSeaLanes={showSeaLanes} selectedYear={selectedYear} />

                        {placeMarkers.map((marker, index) => (
                            <Marker
                                key={`place-${index}`}
                                position={marker.position}
                                icon={createSimplePlaceIcon(marker.place)}
                                ref={(ref) => {
                                    if (ref) {
                                        markerRefs.current.set(marker.place.placeName, ref);
                                    }
                                }}
                                eventHandlers={{
                                    click: () => handlePlaceClick(marker.place.placeName, { latitude: marker.place.coordinates.latitude, longitude: marker.place.coordinates.longitude })
                                }}
                            >
                                {!isMobile && (
                                    <Popup offset={[0, -10]} maxWidth={340} maxHeight={520}>
                                        <PlacePopupContent
                                            placeName={marker.place.placeName}
                                            type={marker.place.type}
                                            summary={marker.place.summary}
                                            description={marker.place.description}
                                            biblicalRelevance={marker.place.biblicalRelevance}
                                            modernReference={marker.place.coordinates.modernReference}
                                            coordinates={{
                                                precision: marker.place.coordinates.precision,
                                                confidence: marker.place.coordinates.confidence
                                            }}
                                            importance={marker.place.importance}
                                            geography={marker.place.geography}
                                            significance={marker.place.significance}
                                            archaeology={marker.place.archaeology}
                                            historicalPeriods={marker.place.historicalPeriods}
                                            biblicalReferences={marker.place.biblicalReferences}
                                            relatedHistoricalEvents={marker.place.relatedHistoricalEvents}
                                            sources={marker.place.sources}
                                        />
                                    </Popup>
                                )}
                                {showLabels && !['region', 'body-of-water', 'desert'].includes(marker.place.type) && (
                                    <Tooltip permanent direction="top" offset={[0, -20]}>
                                        {marker.place.placeName}
                                    </Tooltip>
                                )}
                            </Marker>
                        ))}

                        {/* Event markers */}
                        {eventMarkers.map((marker, index) => (
                            <Marker
                                key={`event-${index}`}
                                position={marker.position}
                                icon={createEventIcon()}
                                ref={(ref) => {
                                    if (ref) {
                                        eventMarkerRefs.current.set(marker.event.title, ref);
                                    }
                                }}
                                eventHandlers={{
                                    click: () => handleEventClick(marker.event.title, marker.matchingPlace.coordinates)
                                }}
                            >
                                {!isMobile && (
                                    <Popup maxWidth={300} offset={[0, -10]}>
                                        <EventPopupContent
                                            title={marker.event.title}
                                            summary={marker.event.summary}
                                            description={marker.event.description}
                                            relevance={marker.event.relevance}
                                            date={marker.event.date}
                                            alternativeDates={marker.event.alternativeDates}
                                            categories={marker.event.categories}
                                            sources={marker.event.sources}
                                        />
                                    </Popup>
                                )}
                            </Marker>
                        ))}
                    </MapContainer>

                    {/* Legend */}
                    <div className="absolute bottom-4 left-4 z-[500]">
                        <MapLegend
                            activePlaceTypes={Array.from(selectedPlaceTypes)}
                            activeEventCategories={Array.from(selectedEventCategories)}
                        />
                    </div>

                    {/* Timeline Control */}
                    <div className="absolute bottom-4 right-4 z-[500]">
                        <MapTimelineControl
                            minYear={timelineData.minYear}
                            maxYear={timelineData.maxYear}
                            currentYear={selectedYear}
                            eras={timelineData.eras}
                            onYearChange={setSelectedYear}
                        />
                    </div>
                </div>

                {/* Sidebar */}
                {sidebarOpen && (
                    <div className="w-80 bg-card border-l border-border overflow-y-auto">
                        <div className="p-4 space-y-6">
                            {/* Map Style Controls */}
                            <div>
                                <h3 className="text-sm font-semibold mb-3">Map Appearance</h3>
                                <MapStyleControl
                                    selectedStyle={selectedStyleId}
                                    onStyleChange={setSelectedStyleId}
                                    showLabels={showLabels}
                                    onShowLabelsChange={setShowLabels}
                                    showAncientRoads={showAncientRoads}
                                    onShowAncientRoadsChange={setShowAncientRoads}
                                    showSeaLanes={showSeaLanes}
                                    onShowSeaLanesChange={setShowSeaLanes}
                                />
                            </div>

                            <Separator />

                            {/* Filters */}
                            <div>
                                <h3 className="text-sm font-semibold mb-3">Filters</h3>
                                <MapFilters
                                    availablePlaceTypes={availablePlaceTypes}
                                    selectedPlaceTypes={selectedPlaceTypes}
                                    onPlaceTypesChange={setSelectedPlaceTypes}
                                    placeTypeCounts={placeTypeCounts}
                                    availableEventCategories={availableEventCategories}
                                    selectedEventCategories={selectedEventCategories}
                                    onEventCategoriesChange={setSelectedEventCategories}
                                    eventCategoryCounts={eventCategoryCounts}
                                />
                            </div>

                            <Separator />

                            {/* Places List */}
                            <div>
                                <h3 className="text-sm font-semibold mb-3">
                                    Places ({visiblePlaces.length})
                                </h3>
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {visiblePlaces.slice(0, 50).map((place, index) => (
                                        <Card
                                            key={index}
                                            className="p-3 cursor-pointer hover:bg-accent transition-colors"
                                            onClick={() => handlePlaceClick(place.placeName, place.coordinates)}
                                        >
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium text-sm">{place.placeName}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground line-clamp-2">
                                                    {place.summary}
                                                </p>
                                            </div>
                                        </Card>
                                    ))}
                                    {visiblePlaces.length > 50 && (
                                        <p className="text-xs text-muted-foreground text-center py-2">
                                            Showing 50 of {visiblePlaces.length} places
                                        </p>
                                    )}
                                </div>
                            </div>

                            <Separator />

                            {/* Events List */}
                            <div>
                                <h3 className="text-sm font-semibold mb-3">
                                    Events ({visibleEvents.length})
                                </h3>
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {eventMarkers.slice(0, 50).map((marker, index) => (
                                        <Card
                                            key={index}
                                            className="p-3 cursor-pointer hover:bg-accent transition-colors"
                                            onClick={() => handleEventClick(marker.event.title, marker.matchingPlace.coordinates)}
                                        >
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium text-sm">{marker.event.title}</span>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                    {marker.event.date.displayDate}
                                                </p>
                                                <p className="text-xs text-muted-foreground line-clamp-2">
                                                    {marker.event.summary}
                                                </p>
                                            </div>
                                        </Card>
                                    ))}
                                    {eventMarkers.length > 50 && (
                                        <p className="text-xs text-muted-foreground text-center py-2">
                                            Showing 50 of {eventMarkers.length} events
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Selection Modals */}
            <MobileEntityModal
                entity={selectedEntityForModal as any}
                eventMarker={selectedEventForModal}
                onClose={() => {
                    setSelectedEntityForModal(null);
                    setSelectedEventForModal(null);
                }}
                passageYear={selectedYear}
                historicalEvents={allEvents}
            />
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="flex flex-col h-[calc(100vh-48px)]">
            <div className="bg-card border-b border-border p-4">
                <Skeleton className="h-8 w-64" />
            </div>
            <div className="flex-1 flex items-center justify-center">
                <div className="text-center space-y-4">
                    <Skeleton className="h-12 w-12 rounded-full mx-auto" />
                    <Skeleton className="h-4 w-48" />
                </div>
            </div>
        </div>
    );
}
